import React, { useRef, useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import './portfolio.css'
import { FiArrowUpRight, FiChevronLeft, FiChevronRight } from 'react-icons/fi'

import IMG1 from '../../assets/portfolio1.png'
import IMG2 from '../../assets/portfolio2.png'
import IMG3 from '../../assets/portfolio3.png'
import IMG4 from '../../assets/portfolio4.png'
import IMG5 from '../../assets/portfolio5.png'

const projects = [
  { id: 1, image: IMG1, title: 'MAKAALB', demo: 'https://makalb.com/' },
  { id: 2, image: IMG2, title: 'E DUA FSHATIN TIM', demo: 'https://eduafshatintim.com/' },
  { id: 3, image: IMG3, title: 'AUTODIENST24', demo: 'https://autodienst24.at/' },
  { id: 4, image: IMG4, title: 'SECURITAS-M', demo: 'https://securitas-m.com/' },
  { id: 5, image: IMG5, title: 'VAOSC', demo: 'https://vaosc.netlify.app/' },
]

const AUTO_MS = 3500

const PortfolioCard = ({ id, image, title, demo, t }) => (
  <a
    href={demo}
    className="portfolio__card glass"
    target="_blank"
    rel="noopener noreferrer"
  >
    <div className="portfolio__media">
      <img src={image} alt={title} loading="lazy" />
      <span className="portfolio__index">{String(id).padStart(2, '0')}</span>
    </div>
    <div className="portfolio__info">
      <h3>{title}</h3>
      <span className="portfolio__link">
        {t('portfolio.liveDemo')}
        <FiArrowUpRight />
      </span>
    </div>
  </a>
)

const Portfolio = () => {
  const { t } = useTranslation()
  const sliderRef = useRef(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const pausedRef = useRef(false)
  const activeRef = useRef(0)

  useEffect(() => {
    pausedRef.current = paused
  }, [paused])

  useEffect(() => {
    activeRef.current = activeIndex
  }, [activeIndex])

  const getCards = useCallback(() => {
    const slider = sliderRef.current
    if (!slider) return []
    return Array.from(slider.querySelectorAll('.portfolio__card'))
  }, [])

  const scrollToIndex = useCallback((index, smooth = true) => {
    const slider = sliderRef.current
    const cards = getCards()
    const card = cards[index]
    if (!slider || !card) return

    const target = card.offsetLeft - (slider.clientWidth - card.offsetWidth) / 2

    slider.scrollTo({
      left: Math.max(0, target),
      behavior: smooth ? 'smooth' : 'auto',
    })
    setActiveIndex(index)
  }, [getCards])

  const goNext = useCallback(() => {
    scrollToIndex((activeIndex + 1) % projects.length)
  }, [activeIndex, scrollToIndex])

  const goPrev = useCallback(() => {
    scrollToIndex((activeIndex - 1 + projects.length) % projects.length)
  }, [activeIndex, scrollToIndex])

  useEffect(() => {
    const slider = sliderRef.current
    if (!slider) return undefined

    const onScroll = () => {
      const cards = getCards()
      if (!cards.length) return

      const center = slider.scrollLeft + slider.clientWidth / 2
      let closest = 0
      let minDist = Infinity

      cards.forEach((card, i) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2
        const dist = Math.abs(center - cardCenter)
        if (dist < minDist) {
          minDist = dist
          closest = i
        }
      })

      setActiveIndex(closest)
    }

    slider.addEventListener('scroll', onScroll, { passive: true })
    return () => slider.removeEventListener('scroll', onScroll)
  }, [getCards])

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reducedMotion) return undefined

    const timer = setInterval(() => {
      if (pausedRef.current) return
      const next = (activeRef.current + 1) % projects.length
      scrollToIndex(next, true)
    }, AUTO_MS)

    return () => clearInterval(timer)
  }, [scrollToIndex])

  useEffect(() => {
    const onResize = () => scrollToIndex(activeIndex, false)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [activeIndex, scrollToIndex])

  return (
    <section id="portfolio" className="portfolio-section">
      <div className="portfolio-section__glow" aria-hidden="true" />
      <div className="container portfolio__header-wrap">
        <div className="section__header">
          <span className="section__label">{t('portfolio.subtitle')}</span>
          <h2 className="section__title">{t('portfolio.title')}</h2>
        </div>

        <div className="portfolio__controls">
          <button type="button" className="portfolio__nav" onClick={goPrev} aria-label="Previous project">
            <FiChevronLeft />
          </button>
          <button type="button" className="portfolio__nav" onClick={goNext} aria-label="Next project">
            <FiChevronRight />
          </button>
        </div>
      </div>

      <div
        className="portfolio__viewport"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setTimeout(() => setPaused(false), 3000)}
      >
        <div className="portfolio__fade portfolio__fade--left" aria-hidden="true" />
        <div className="portfolio__fade portfolio__fade--right" aria-hidden="true" />

        <div className="portfolio__slider" ref={sliderRef}>
          <div className="portfolio__track">
            {projects.map((project) => (
              <PortfolioCard key={project.id} {...project} t={t} />
            ))}
          </div>
        </div>
      </div>

      <div className="container portfolio__dots">
        {projects.map(({ id, title }, index) => (
          <button
            key={id}
            type="button"
            className={`portfolio__dot ${activeIndex === index ? 'active' : ''}`}
            onClick={() => scrollToIndex(index)}
            aria-label={title}
          />
        ))}
      </div>
    </section>
  )
}

export default Portfolio
